// The map's misc_gamemodels (CG_AddMiscGameModels in cg_draw.c): MD3 / MDC
// models with their scaled axes (nonNormalizedAxes), lit from the light grid
// at their origin like any entity (no far-away hilight), drawn while their
// origin is in the camera's PVS. Animated ones loop their frames.

import * as THREE from 'three';
import type { VirtualFS } from '../fs/vfs';
import { clusterVisible, pointInLeaf, type Bsp } from '../shared/bsp';
import { gameModelDefs, gameModelFrame, type GameModelDef } from '../shared/gameModels';
import type { Entity } from '../shared/entities';
import { angleVectors, type Vec3 } from '../shared/math';
import { EntityRenderKit, MeshPart } from './entityParts';
import { modelFile } from './models/character';
import { parseMd3, type MeshModel } from './models/md3';
import { parseMdc } from './models/mdc';
import type { Axis } from './models/skeleton';

interface Placed {
  def: GameModelDef;
  part: MeshPart;
  cluster: number;
}

/** AnglesToAxis: forward, left, up. */
function anglesToAxis(angles: Vec3): Axis {
  const f: Vec3 = [0, 0, 0];
  const r: Vec3 = [0, 0, 0];
  const u: Vec3 = [0, 0, 0];
  angleVectors(angles, f, r, u);
  return [f, [-r[0], -r[1], -r[2]], u];
}

/** Model bounds of the first frame, scaled. */
function radiusOf(model: MeshModel, scale: Vec3): number {
  let r = 0;
  for (const s of model.surfaces) {
    for (let v = 0; v < s.numVerts; v++) {
      r = Math.max(r, Math.hypot(s.xyz[v * 3] * scale[0], s.xyz[v * 3 + 1] * scale[1], s.xyz[v * 3 + 2] * scale[2]));
    }
  }
  return r || 32;
}

export class MapModels {
  readonly object = new THREE.Group();
  private readonly placed: Placed[] = [];
  private readonly animated: Placed[] = [];

  private constructor(
    readonly kit: EntityRenderKit,
    private readonly bsp: Bsp,
  ) {}

  /** Loads the gamemodels of the entities (missing or broken models are skipped). */
  static async load(entities: Entity[], vfs: VirtualFS, kit: EntityRenderKit, bsp: Bsp): Promise<MapModels> {
    const out = new MapModels(kit, bsp);
    const models = new Map<string, MeshModel | null>();
    for (const def of gameModelDefs(entities)) {
      let model = models.get(def.model);
      if (model === undefined) {
        model = null;
        const path = modelFile((p) => vfs.has(p), def.model);
        const data = path ? await vfs.read(path) : null;
        if (path && data) {
          try {
            model = path.endsWith('.mdc') ? parseMdc(data, path) : parseMd3(data, path);
          } catch (err) {
            console.warn(`misc_gamemodel ${def.model}:`, err);
          }
        }
        models.set(def.model, model);
      }
      if (model) out.add(def, model);
    }
    return out;
  }

  private add(def: GameModelDef, model: MeshModel): void {
    const part = new MeshPart(this.object, this.kit, model, null, false, radiusOf(model, def.scale));
    const axis = anglesToAxis(def.angles);
    // lit with unit axes (the light direction in model space), drawn with the scaled ones
    part.place({ origin: def.origin, axis });
    part.light(this.kit.light(def.origin, def.origin, false));
    part.place({ origin: def.origin, axis: axis.map((a, i) => a.map((x) => x * def.scale[i])) as Axis });
    const leaf = this.bsp.leafs[pointInLeaf(this.bsp, def.origin)];
    const p: Placed = { def, part, cluster: leaf ? leaf.cluster : -1 };
    this.placed.push(p);
    if (def.animate) this.animated.push(p);
  }

  get count(): number {
    return this.placed.length;
  }

  /** Animated models' frames (time in seconds, the map's shader clock). */
  update(time: number): void {
    for (const p of this.animated) {
      const f = gameModelFrame(p.def.animate!, time);
      p.part.setFrame(f.next, f.frame, 1 - f.lerp);
      const axis = p.part.orientation.axis;
      p.part.place({ origin: p.def.origin, axis: anglesToAxis(p.def.angles) });
      p.part.light(this.kit.light(p.def.origin, p.def.origin, false));
      p.part.place({ origin: p.def.origin, axis });
    }
  }

  /** trap_R_inPVS of each model's origin from the camera's cluster (-1: all visible). */
  updateVisibility(cluster: number): void {
    const vis = this.bsp.visibility;
    for (const p of this.placed) p.part.group.visible = cluster < 0 || !vis || p.cluster < 0 || clusterVisible(vis, cluster, p.cluster);
  }

  dispose(): void {
    for (const p of this.placed) p.part.dispose();
    this.kit.dispose();
  }
}
